import { type IWebAnalyticsConsentEstimate } from '@mahawi/eshop-common';
import { Card, Space, Statistic, Tag, Typography } from 'antd';
import React from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { type ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { COLOR, FONT_SIZE, SPACING } from '../../theme/tokens';
import { formatRatio } from './format';

const { Text } = Typography;

export const formatRatioTick = (value: number): string =>
  `${Math.round(value * 100)} %`;

export const formatRatioTooltip = (
  value: ValueType | null | undefined,
): string => formatRatio(typeof value === 'number' ? value : null);

function ConsentEstimate({
  estimate,
}: {
  estimate: IWebAnalyticsConsentEstimate;
}): React.ReactElement {
  return (
    <Card
      size="small"
      title={
        <Space size={SPACING.sm}>
          Consent rate
          <Tag color="gold">estimate</Tag>
        </Space>
      }
    >
      <Space size={SPACING.xl} wrap>
        <Statistic
          title="GA4 organic sessions ÷ search clicks"
          value={formatRatio(estimate.organicRatio)}
        />
        <Statistic
          title="GA4 page views ÷ Cloudflare page views"
          value={formatRatio(estimate.pageViewRatio)}
        />
      </Space>
      <Text type="secondary">
        GA4 only sees visitors who accepted analytics cookies; the ratios
        approximate that share and are not exact.
      </Text>
      <ResponsiveContainer width="100%" height={140}>
        <LineChart
          data={estimate.series}
          margin={{ top: SPACING.sm, right: SPACING.sm, bottom: 0, left: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={COLOR.borderDefault} />
          <XAxis dataKey="date" tick={{ fontSize: FONT_SIZE.xs }} />
          <YAxis
            tick={{ fontSize: FONT_SIZE.xs }}
            width={50}
            tickFormatter={formatRatioTick}
          />
          <RechartsTooltip formatter={formatRatioTooltip} />
          <Line
            type="monotone"
            dataKey="organicRatio"
            name="Organic"
            stroke={COLOR.primary}
            dot={false}
            connectNulls={false}
          />
          <Line
            type="monotone"
            dataKey="pageViewRatio"
            name="Page views"
            stroke={COLOR.gold[5]}
            dot={false}
            connectNulls={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
}

export default ConsentEstimate;
